/**
 * Secure Key Storage for Hyperliquid API Wallet Private Keys
 *
 * Uses IndexedDB + Web Crypto API to encrypt private keys at rest.
 * Encryption key is derived from the user's Dynamic.xyz embedded wallet session,
 * providing protection against browser extensions and XSS attacks at rest.
 *
 * Security properties:
 * - Browser extensions cannot access IndexedDB across origins
 * - Encrypted data is useless without active session signature
 * - Non-extractable CryptoKey prevents JS exfiltration
 * - Attack surface limited to when key is actively in use
 */

import type { WalletClient } from "viem";

// Constants
const DB_NAME = "perpsstudio-secure-storage";
const DB_VERSION = 1;
const STORE_NAME = "encrypted-keys";
const METADATA_STORE_NAME = "key-metadata";
// Versioned key derivation config — enables rotation to new derivation params
interface KeyVersion {
  message: string;
  iterations: number;
}

export const KEY_VERSIONS: Record<number, KeyVersion> = {
  2: { message: "perpsstudio:derive-encryption-key:v2", iterations: 600_000 },
};

export const CURRENT_KEY_VERSION = 2;

// Types
export interface EncryptedKeyRecord {
  encryptedData: ArrayBuffer;
  iv: Uint8Array;
  salt: Uint8Array;
  version: number;
}

export interface KeyMetadata {
  address: string;
  isApproved: boolean;
  approvedAt?: string;
  createdAt: string;
}

export interface SecureKeyStorageInterface {
  storeKey(
    userAddress: string,
    privateKey: string,
    apiWalletAddress: string,
    walletClient: WalletClient,
    metadata?: Partial<KeyMetadata>,
  ): Promise<void>;

  retrieveKey(
    userAddress: string,
    walletClient: WalletClient,
  ): Promise<{ privateKey: string; metadata: KeyMetadata } | null>;

  hasKey(userAddress: string): Promise<boolean>;

  clearKey(userAddress: string): Promise<void>;

  getMetadata(userAddress: string): Promise<KeyMetadata | null>;

  updateMetadata(
    userAddress: string,
    updates: Partial<KeyMetadata>,
  ): Promise<void>;

  rotateKey(
    userAddress: string,
    walletClient: WalletClient,
    targetVersion?: number,
  ): Promise<void>;
}

/**
 * Open or create the IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "userAddress" });
      }

      if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
        db.createObjectStore(METADATA_STORE_NAME, { keyPath: "userAddress" });
      }
    };
  });
}

/**
 * Derive an encryption key from a wallet signature.
 * Uses PBKDF2 with the signature as key material.
 *
 * The version parameter selects the derivation message and iteration count
 * from KEY_VERSIONS. Defaults to CURRENT_KEY_VERSION for new encryptions.
 * When decrypting, pass the version from the stored record.
 */
async function deriveEncryptionKey(
  walletClient: WalletClient,
  salt: Uint8Array,
  version: number = CURRENT_KEY_VERSION,
): Promise<CryptoKey> {
  const config = KEY_VERSIONS[version];
  if (!config) {
    throw new Error(`Unknown key version: ${version}`);
  }

  const account = walletClient.account;
  if (!account) {
    throw new Error("Wallet client has no account");
  }

  const signature = await walletClient.signMessage({
    account,
    message: config.message,
  });

  const encoder = new TextEncoder();
  const signatureBytes = encoder.encode(signature);

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    signatureBytes,
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  const encryptionKey = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as unknown as BufferSource,
      iterations: config.iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false, // non-extractable
    ["encrypt", "decrypt"],
  );

  return encryptionKey;
}

/**
 * Encrypt data using AES-GCM
 */
async function encryptData(
  data: string,
  key: CryptoKey,
  iv: Uint8Array,
): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const dataBytes = encoder.encode(data);

  return await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as unknown as BufferSource },
    key,
    dataBytes,
  );
}

/**
 * Decrypt data using AES-GCM
 */
async function decryptData(
  encryptedData: ArrayBuffer,
  key: CryptoKey,
  iv: Uint8Array,
): Promise<string> {
  const decryptedBytes = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as unknown as BufferSource },
    key,
    encryptedData,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBytes);
}

/**
 * Generate cryptographically secure random bytes
 */
function generateRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Normalize user address for consistent storage key
 */
function normalizeAddress(address: string): string {
  return address.toLowerCase();
}

/**
 * Secure Key Storage implementation
 */
export class SecureKeyStorage implements SecureKeyStorageInterface {
  async storeKey(
    userAddress: string,
    privateKey: string,
    apiWalletAddress: string,
    walletClient: WalletClient,
    metadata?: Partial<KeyMetadata>,
  ): Promise<void> {
    const normalizedAddress = normalizeAddress(userAddress);

    const salt = generateRandomBytes(32);
    const iv = generateRandomBytes(12); // 96 bits for AES-GCM

    const encryptionKey = await deriveEncryptionKey(walletClient, salt);
    const encryptedData = await encryptData(privateKey, encryptionKey, iv);

    const db = await openDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_NAME, METADATA_STORE_NAME],
        "readwrite",
      );

      transaction.onerror = () => {
        reject(new Error(`Failed to store key: ${transaction.error?.message}`));
      };

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      const keyStore = transaction.objectStore(STORE_NAME);
      const keyRecord: EncryptedKeyRecord & { userAddress: string } = {
        userAddress: normalizedAddress,
        encryptedData,
        iv,
        salt,
        version: CURRENT_KEY_VERSION,
      };
      keyStore.put(keyRecord);

      const metadataStore = transaction.objectStore(METADATA_STORE_NAME);
      const metadataRecord: KeyMetadata & { userAddress: string } = {
        userAddress: normalizedAddress,
        address: apiWalletAddress,
        isApproved: metadata?.isApproved ?? false,
        approvedAt: metadata?.approvedAt,
        createdAt: metadata?.createdAt ?? new Date().toISOString(),
      };
      metadataStore.put(metadataRecord);
    });
  }

  async retrieveKey(
    userAddress: string,
    walletClient: WalletClient,
  ): Promise<{ privateKey: string; metadata: KeyMetadata } | null> {
    const normalizedAddress = normalizeAddress(userAddress);
    const db = await openDatabase();

    const record = await new Promise<
      (EncryptedKeyRecord & { userAddress: string }) | null
    >((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(normalizedAddress);

      request.onerror = () => {
        reject(new Error(`Failed to retrieve key: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });

    if (!record) {
      db.close();
      return null;
    }

    const metadata = await new Promise<
      (KeyMetadata & { userAddress: string }) | null
    >((resolve, reject) => {
      const transaction = db.transaction(METADATA_STORE_NAME, "readonly");
      const store = transaction.objectStore(METADATA_STORE_NAME);
      const request = store.get(normalizedAddress);

      request.onerror = () => {
        reject(
          new Error(`Failed to retrieve metadata: ${request.error?.message}`),
        );
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });

    db.close();

    if (!metadata) {
      return null;
    }

    try {
      const encryptionKey = await deriveEncryptionKey(
        walletClient,
        record.salt,
        record.version,
      );

      const privateKey = await decryptData(
        record.encryptedData,
        encryptionKey,
        record.iv,
      );

      return {
        privateKey,
        metadata: {
          address: metadata.address,
          isApproved: metadata.isApproved,
          approvedAt: metadata.approvedAt,
          createdAt: metadata.createdAt,
        },
      };
    } catch (error) {
      console.error("Failed to decrypt key:", error);
      return null;
    }
  }

  async hasKey(userAddress: string): Promise<boolean> {
    const normalizedAddress = normalizeAddress(userAddress);

    try {
      const db = await openDatabase();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.count(IDBKeyRange.only(normalizedAddress));

        request.onerror = () => {
          db.close();
          reject(new Error(`Failed to check key: ${request.error?.message}`));
        };

        request.onsuccess = () => {
          db.close();
          resolve(request.result > 0);
        };
      });
    } catch {
      return false;
    }
  }

  async clearKey(userAddress: string): Promise<void> {
    const normalizedAddress = normalizeAddress(userAddress);
    const db = await openDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_NAME, METADATA_STORE_NAME],
        "readwrite",
      );

      transaction.onerror = () => {
        reject(new Error(`Failed to clear key: ${transaction.error?.message}`));
      };

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      const keyStore = transaction.objectStore(STORE_NAME);
      keyStore.delete(normalizedAddress);

      const metadataStore = transaction.objectStore(METADATA_STORE_NAME);
      metadataStore.delete(normalizedAddress);
    });
  }

  async getMetadata(userAddress: string): Promise<KeyMetadata | null> {
    const normalizedAddress = normalizeAddress(userAddress);

    try {
      const db = await openDatabase();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(METADATA_STORE_NAME, "readonly");
        const store = transaction.objectStore(METADATA_STORE_NAME);
        const request = store.get(normalizedAddress);

        request.onerror = () => {
          db.close();
          reject(
            new Error(`Failed to get metadata: ${request.error?.message}`),
          );
        };

        request.onsuccess = () => {
          db.close();
          const result = request.result as
            | (KeyMetadata & { userAddress: string })
            | undefined;
          if (result) {
            resolve({
              address: result.address,
              isApproved: result.isApproved,
              approvedAt: result.approvedAt,
              createdAt: result.createdAt,
            });
          } else {
            resolve(null);
          }
        };
      });
    } catch {
      return null;
    }
  }

  async updateMetadata(
    userAddress: string,
    updates: Partial<KeyMetadata>,
  ): Promise<void> {
    const normalizedAddress = normalizeAddress(userAddress);
    const db = await openDatabase();

    const existing = await new Promise<
      (KeyMetadata & { userAddress: string }) | null
    >((resolve, reject) => {
      const transaction = db.transaction(METADATA_STORE_NAME, "readonly");
      const store = transaction.objectStore(METADATA_STORE_NAME);
      const request = store.get(normalizedAddress);

      request.onerror = () => {
        reject(new Error(`Failed to get metadata: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });

    if (!existing) {
      db.close();
      throw new Error("No metadata found for address");
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(METADATA_STORE_NAME, "readwrite");
      const store = transaction.objectStore(METADATA_STORE_NAME);

      const updated: KeyMetadata & { userAddress: string } = {
        ...existing,
        ...updates,
        userAddress: normalizedAddress,
      };

      const request = store.put(updated);

      request.onerror = () => {
        db.close();
        reject(
          new Error(`Failed to update metadata: ${request.error?.message}`),
        );
      };

      request.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  }

  /**
   * Rotate the encryption key to a new version.
   *
   * Decrypts the private key using the stored version's parameters,
   * then re-encrypts with the target version's message, salt, IV, and
   * iteration count. Writes the migrated record back to IndexedDB.
   *
   * Use for manual rotation when the user suspects compromise, or
   * to force migration to a stronger derivation version.
   */
  async rotateKey(
    userAddress: string,
    walletClient: WalletClient,
    targetVersion: number = CURRENT_KEY_VERSION,
  ): Promise<void> {
    const targetConfig = KEY_VERSIONS[targetVersion];
    if (!targetConfig) {
      throw new Error(`Unknown target key version: ${targetVersion}`);
    }

    const normalizedAddress = normalizeAddress(userAddress);
    const db = await openDatabase();

    // 1. Read the current encrypted record
    const record = await new Promise<
      (EncryptedKeyRecord & { userAddress: string }) | null
    >((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(normalizedAddress);

      request.onerror = () => {
        reject(
          new Error(
            `Failed to read key for rotation: ${request.error?.message}`,
          ),
        );
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });

    if (!record) {
      db.close();
      throw new Error("No key found for address");
    }

    // 2. Decrypt with the stored version
    const oldKey = await deriveEncryptionKey(
      walletClient,
      record.salt,
      record.version,
    );
    const privateKey = await decryptData(
      record.encryptedData,
      oldKey,
      record.iv,
    );

    // 3. Re-encrypt with target version (new salt, new IV, new derivation params)
    const newSalt = generateRandomBytes(32);
    const newIv = generateRandomBytes(12);
    const newKey = await deriveEncryptionKey(
      walletClient,
      newSalt,
      targetVersion,
    );
    const newEncryptedData = await encryptData(privateKey, newKey, newIv);

    // 4. Write back to IndexedDB
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      transaction.onerror = () => {
        reject(
          new Error(
            `Failed to write rotated key: ${transaction.error?.message}`,
          ),
        );
      };
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      const store = transaction.objectStore(STORE_NAME);
      const rotatedRecord: EncryptedKeyRecord & { userAddress: string } = {
        userAddress: normalizedAddress,
        encryptedData: newEncryptedData,
        iv: newIv,
        salt: newSalt,
        version: targetVersion,
      };
      store.put(rotatedRecord);
    });
  }
}

// Singleton instance
export const secureKeyStorage = new SecureKeyStorage();
